import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { COUNTRY_CODES, countryName, rwLabel } from '@/constants/remoteWork';
import { TAX_COUNTRIES, TAX_HINTS } from '@/constants/taxIds';

export default function Register() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { registerEmployer } = useAuth();
  // Países fora da lista só pelo pedido «Empresas internacionais» (?intl=1)
  const intl = searchParams.get('intl') === '1';
  const allowedCountries = intl ? COUNTRY_CODES : TAX_COUNTRIES;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: '',
    fiscal_nif: '',
    company_country: (intl ? COUNTRY_CODES : TAX_COUNTRIES).includes((searchParams.get('country') || '').toUpperCase()) ? searchParams.get('country').toUpperCase() : 'CV',
    phone: '',
    location: '',
    language_preference: i18n.language,
    business_category: '',
    referral_code: searchParams.get('ref') || '',
  });
  const [categories, setCategories] = useState([]);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const isCV = formData.company_country === 'CV';
  const L = (k) => rwLabel(i18n.language, k);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/categories`)
      .then((r) => setCategories(r.data.items || []))
      .catch(() => setCategories([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptTerms) {
      setError(t('auth.acceptTermsRequired'));
      return;
    }
    const nif = isCV ? formData.fiscal_nif.replace(/\s/g, '') : formData.fiscal_nif.trim();
    if (!nif) {
      setError(isCV ? t('auth.nifRequired') : L('foreignNifRequired'));
      return;
    }
    if (isCV && !/^\d{9}$/.test(nif)) {
      setError(L('nifCvInvalid'));
      return;
    }
    setLoading(true);

    const result = await registerEmployer({ ...formData, fiscal_nif: nif, international_request: intl && !TAX_COUNTRIES.includes(formData.company_country) });

    if (result.verificationRequired) {
      navigate('/verify-email', { state: { email: result.email, userType: result.userType } });
      return;
    }
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="mb-6">
            <h1 className="text-5xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
                Trab
              </span>
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                adju
              </span>
            </h1>
            <p className="text-xs text-gray-500 tracking-widest uppercase mt-1">Cabo Verde</p>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">{t('auth.registerTitle')}</h2>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="register-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="register-email-input"
            />
          </div>

          <div>
            <Label htmlFor="password">{t('auth.password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="register-password-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="company_name">{t('auth.companyName')}</Label>
            <Input
              id="company_name"
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              required
              data-testid="register-company-input"
            />
          </div>

          <div>
            <Label htmlFor="company_country">{L('country')}</Label>
            <select
              id="company_country"
              value={formData.company_country}
              onChange={(e) => setFormData({ ...formData, company_country: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="register-country-select"
            >
              {allowedCountries.map((c) => <option key={c} value={c}>{countryName(c, i18n.language)}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1" data-testid="register-country-hint">
              {TAX_HINTS[formData.company_country] || 'Empresa internacional: o registo comercial é verificado pela equipa Trabadju antes de as vagas ficarem visíveis.'}
              {!intl && <> Outro país? <Link to="/empresas-internacionais" className="text-blue-600 underline" data-testid="register-intl-link">Empresas internacionais</Link></>}
            </p>
          </div>

          <div>
            <Label htmlFor="fiscal_nif" data-testid="register-nif-label">
              {isCV ? t('auth.nifLabel') : L('foreignNifLabel')} <span className="text-red-500 text-xs font-medium">*</span>
              <span className="text-gray-400 text-xs ml-1">{isCV ? t('auth.nifHint') : L('foreignNifHint')}</span>
            </Label>
            <Input
              id="fiscal_nif"
              type="text"
              value={formData.fiscal_nif}
              onChange={(e) => setFormData({ ...formData, fiscal_nif: e.target.value })}
              placeholder={isCV ? t('auth.nifPlaceholder') : L('foreignNifPlaceholder')}
              inputMode={isCV ? 'numeric' : 'text'}
              maxLength={isCV ? 9 : 40}
              required
              data-testid="register-nif-input"
            />
          </div>

          <div>
            <Label htmlFor="phone">{t('auth.phone')}</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+238 ..."
              data-testid="register-phone-input"
            />
          </div>

          <div>
            <Label htmlFor="location">{t('auth.location')}</Label>
            <Input
              id="location"
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder={isCV ? 'Praia, Santiago' : 'Lisboa'}
              data-testid="register-location-input"
            />
          </div>

          <div>
            <Label htmlFor="business_category">
              {t('auth.businessCategoryLabel')} <span className="text-gray-400 text-xs">{t('auth.businessCategoryHint')}</span>
            </Label>
            <select
              id="business_category"
              value={formData.business_category}
              onChange={(e) => setFormData({ ...formData, business_category: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="register-category-select"
            >
              <option value="">{t('auth.chooseLaterOption')}</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name_pt}</option>
              ))}
            </select>
          </div>

          <div className="flex items-start gap-2 pt-2">
            <Checkbox
              id="accept-terms"
              checked={acceptTerms}
              onCheckedChange={setAcceptTerms}
              data-testid="register-accept-terms-checkbox"
              className="mt-0.5"
            />
            <Label htmlFor="accept-terms" className="text-sm font-normal leading-snug cursor-pointer">
              {t('auth.acceptTermsPre')}{' '}
              <Link to="/termos" target="_blank" className="text-blue-600 hover:underline">{t('auth.termsLink')}</Link>{' '}
              {t('auth.acceptTermsAnd')}{' '}
              <Link to="/privacidade" target="_blank" className="text-blue-600 hover:underline">{t('auth.privacyLink')}</Link>.
            </Label>
          </div>

          {/* Campo Código do Promotor / Embaixador */}
          <div>
            <Label htmlFor="referral_code" className="text-sm text-gray-600">
              Código do Promotor / Embaixador{' '}
              <span className="text-gray-400 text-xs">(Opcional)</span>
            </Label>
            <Input
              id="referral_code"
              type="text"
              value={formData.referral_code}
              onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
              placeholder="Ex: TRAB-XXXX"
              className={formData.referral_code ? 'border-emerald-400 bg-emerald-50' : ''}
              data-testid="register-referral-input"
            />
            {formData.referral_code && (
              <p className="text-xs text-emerald-600 mt-1">Código de embaixador detectado!</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={loading || !acceptTerms}
            data-testid="register-submit-btn"
          >
            {loading ? t('common.loading') : t('auth.registerButton')}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          <span className="text-gray-600">{t('auth.hasAccount')} </span>
          <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
            {t('auth.loginButton')}
          </Link>
        </div>
      </Card>
    </div>
  );
}
